import { IconChevronLeft, IconChevronRight } from "@dream/ui";
import { DateTime } from "luxon";

/**
 * Mini calendário mensal da sidebar — chrome da frame Ofative (58:2316).
 *
 * O dia atual ganha o círculo sólido `#0074FF`. O dia em foco (URL) ganha o
 * ponto azul abaixo, como no kit.
 */

const WEEKDAYS_MON = ["Se", "Te", "Qa", "Qi", "Sx", "Sa", "Do"] as const;
const WEEKDAYS_SUN = ["Do", "Se", "Te", "Qa", "Qi", "Sx", "Sa"] as const;

export interface MiniMonthProps {
  focusDate: Date;
  timezone: string;
  onSelect: (date: Date) => void;
  onShiftMonth: (delta: number) => void;
  /** 1 = segunda (padrão Ofative/ISO), 0 = domingo. */
  weekStartsOn?: 0 | 1;
}

export function MiniMonth({
  focusDate,
  timezone,
  onSelect,
  onShiftMonth,
  weekStartsOn = 1,
}: MiniMonthProps) {
  const focus = DateTime.fromJSDate(focusDate, { zone: timezone });
  const month = focus.startOf("month");
  const today = DateTime.now().setZone(timezone).startOf("day");
  const labels = weekStartsOn === 0 ? WEEKDAYS_SUN : WEEKDAYS_MON;
  const startOffset = weekStartsOn === 0 ? month.weekday % 7 : month.weekday - 1;
  const daysInMonth = month.daysInMonth ?? 30;
  const cells: Array<{ slot: string; day: number | null; date: DateTime | null }> = [];

  for (let i = 0; i < startOffset; i++) {
    cells.push({ slot: `lead-${i}`, day: null, date: null });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const date = month.set({ day });
    cells.push({ slot: date.toISODate() ?? `d-${day}`, day, date });
  }
  let trail = 0;
  while (cells.length % 7 !== 0) {
    cells.push({ slot: `trail-${trail}`, day: null, date: null });
    trail += 1;
  }

  return (
    <div className="px-1">
      <div className="mb-2 flex items-center justify-between">
        <p
          key={month.toFormat("yyyy-LL")}
          className="dream-period-in min-w-0 truncate text-[16px] font-bold capitalize tracking-[0] text-[var(--color-ink)]"
        >
          {month.setLocale("pt-BR").toFormat("LLLL yyyy")}
        </p>
        <div className="flex gap-0.5">
          <button
            type="button"
            aria-label="Mês anterior"
            onClick={() => onShiftMonth(-1)}
            className="flex size-7 items-center justify-center rounded-lg text-[var(--color-ink-muted)] transition-[background-color,color,transform] duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface)] hover:text-[var(--color-ink)] active:scale-95"
          >
            <IconChevronLeft className="size-3.5" />
          </button>
          <button
            type="button"
            aria-label="Próximo mês"
            onClick={() => onShiftMonth(1)}
            className="flex size-7 items-center justify-center rounded-lg text-[var(--color-ink-muted)] transition-[background-color,color,transform] duration-[var(--dream-duration-fast)] hover:bg-[var(--color-surface)] hover:text-[var(--color-ink)] active:scale-95"
          >
            <IconChevronRight className="size-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center">
        {labels.map((label) => (
          <span key={label} className="py-1 text-[12px] font-medium text-[var(--color-ink)]">
            {label}
          </span>
        ))}
        {cells.map((cell) => {
          if (cell.date === null || cell.day === null) {
            return <span key={`empty-${cell.slot}`} />;
          }

          const isToday = cell.date.hasSame(today, "day");
          const isFocus = cell.date.hasSame(focus, "day");
          const date = cell.date;

          return (
            <button
              key={date.toISODate()}
              type="button"
              onClick={() => onSelect(date.toJSDate())}
              className={[
                "relative mx-auto flex size-[31px] items-center justify-center rounded-full text-[14px] font-medium",
                "transition-[background-color,color,transform] duration-[var(--dream-duration-fast)] ease-[var(--dream-ease-standard)]",
                isToday
                  ? "bg-[#0074FF] font-medium text-white"
                  : "text-[var(--color-ink)] hover:scale-105 hover:bg-[var(--color-surface)]",
              ].join(" ")}
            >
              {cell.day}
              {isFocus && !isToday && (
                <span className="absolute bottom-0.5 size-1 rounded-full bg-brand-600" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
